import React, { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { fetchNBAStandings, fetchPLStandings } from './api';
import Tooltip from './components/Tooltip';
import './Standings.css';

const Standings = () => {
    const location = useLocation();
    const navigate = useNavigate();
    const searchParams = new URLSearchParams(location.search);
    const leagueParam = searchParams.get('league');
    const initialLeague = (leagueParam && leagueParam.includes('Premier')) ? 'Premier League' : 'NBA';
    
    const [standings, setStandings] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [selectedLeague, setSelectedLeague] = useState(initialLeague);
    const [selectedConference, setSelectedConference] = useState('ALL');
    const [selectedTeam, setSelectedTeam] = useState(null);
    const [sortBy, setSortBy] = useState(initialLeague === 'NBA' ? 'eloRating' : 'position');
    const [searchQuery, setSearchQuery] = useState('');
    const standingsLoadedRef = useRef(false);
    const retryRef = useRef(0);

    const loadStandings = useCallback(async function loadStandingsWithRetry() {
        if (standingsLoadedRef.current) return;

        standingsLoadedRef.current = true;
        setLoading(true);
        setError(null);
        try {
            const data = selectedLeague === 'NBA' ? await fetchNBAStandings() : await fetchPLStandings();
            setStandings(data);
            retryRef.current = 0;
        } catch (err) {
            console.error(`Error loading ${selectedLeague} standings:`, err);
            setError('Failed to load standings data');
            standingsLoadedRef.current = false; // allow retry
            retryRef.current += 1;
            if (retryRef.current <= 2) {
                setTimeout(loadStandingsWithRetry, 1000 * retryRef.current);
            }
        } finally {
            setLoading(false);
        }
    }, [selectedLeague]);

    useEffect(() => {
        loadStandings();
    }, [loadStandings]);

    // Reset and reload when league changes
    useEffect(() => {
        standingsLoadedRef.current = false;
        retryRef.current = 0;
        setStandings([]);
        setSelectedTeam(null);
        if (selectedLeague === 'NBA') {
            setSelectedConference('ALL');
            setSortBy('eloRating'); // Default to Elo for NBA
        } else {
            setSelectedConference('ALL'); // Not used for PL but reset anyway
            setSortBy('position'); // Default to League Record for Premier League
        }
        loadStandings();
    }, [selectedLeague, loadStandings]);

    // Memoized filtered and sorted data for performance
    const processedStandings = useMemo(() => {
        let filtered = standings.filter(team => {
            // For NBA, filter by conference; for PL, no conference filtering
            const matchesConference = selectedLeague === 'NBA' 
                ? (selectedConference === 'ALL' || team.conference === selectedConference)
                : true;
            const matchesSearch = searchQuery === '' || 
                team.teamFullName.toLowerCase().includes(searchQuery.toLowerCase()) ||
                team.team.toLowerCase().includes(searchQuery.toLowerCase());
            return matchesConference && matchesSearch;
        });

        return filtered.sort((a, b) => {
            switch (sortBy) {
                case 'eloRating':
                    return b.eloRating - a.eloRating;
                case 'playoffProb':
                    if (selectedLeague === 'NBA') {
                        return b.playoffProb - a.playoffProb;
                    } else {
                        return (b.top4Prob || 0) - (a.top4Prob || 0);
                    }
                case 'wins':
                    if (selectedLeague === 'NBA') {
                        return (b.currentRecord?.wins || b.wins || 0) - (a.currentRecord?.wins || a.wins || 0);
                    } else {
                        return (b.points || 0) - (a.points || 0);
                    }
                case 'position':
                    return (a.position || 0) - (b.position || 0);
                case 'alphabetical':
                    return a.teamFullName.localeCompare(b.teamFullName);
                default:
                    return 0;
            }
        });
    }, [standings, selectedConference, sortBy, searchQuery, selectedLeague]);

    // Memoized stats for performance
    const leagueStats = useMemo(() => {
        if (!standings.length) return null;
        
        if (selectedLeague === 'NBA') {
            return {
                totalTeams: standings.length,
                eastTeams: standings.filter(t => t.conference === 'East').length,
                westTeams: standings.filter(t => t.conference === 'West').length,
                strongContenders: standings.filter(t => t.playoffProb > 0.9).length,
                championshipContenders: standings.filter(t => t.championshipProb > 0.1).length,
                averageElo: Math.round(standings.reduce((sum, t) => sum + t.eloRating, 0) / standings.length),
                topTeam: standings.reduce((prev, current) => 
                    prev.eloRating > current.eloRating ? prev : current
                )
            };
        } else {
            // Premier League stats
            const leader = standings.find(t => t.position === 1);
            return {
                totalTeams: standings.length,
                leader: leader?.teamFullName,
                leaderPoints: leader?.points,
                titleRace: standings.filter(t => (t.titleProb || 0) > 0.1).length,
                top4Race: standings.filter(t => (t.top4Prob || 0) > 0.3).length,
                relegationBattle: standings.filter(t => (t.relegationProb || 0) > 0.3).length,
                averageElo: Math.round(standings.reduce((sum, t) => sum + t.eloRating, 0) / standings.length),
                topTeam: standings.reduce((prev, current) => 
                    prev.eloRating > current.eloRating ? prev : current
                )
            };
        }
    }, [standings, selectedLeague]);

    const handleTeamClick = useCallback((teamName) => {
        setSelectedTeam(prev => prev === teamName ? null : teamName);
    }, []);

    const getPerformanceTier = useCallback((team) => {
        if (selectedLeague === 'NBA') {
            if (team.playoffProb >= 0.95) return 'elite';
            if (team.playoffProb >= 0.80) return 'strong';
            if (team.playoffProb >= 0.50) return 'competitive';
            if (team.playoffProb >= 0.20) return 'developing';
            return 'rebuilding';
        } else {
            // Premier League tiers based on position
            if (team.position <= 4) return 'elite'; // UCL
            if (team.position <= 7) return 'strong'; // Europa
            if (team.position <= 14) return 'competitive'; // Mid-table
            if (team.position <= 17) return 'developing'; // Lower mid-table
            return 'rebuilding'; // Relegation zone
        }
    }, [selectedLeague]);

    const getEloTrend = useCallback((change) => {
        if (change > 15) return 'hot';
        if (change > 0) return 'rising';
        if (change < -15) return 'cold';
        if (change < 0) return 'falling';
        return 'stable';
    }, []);

    // Helper function to get shorter team names for mobile display
    const getShortTeamName = useCallback((fullName) => {
        if (!fullName) return '';
        
        // Common NBA team name mappings for mobile
        const teamNameMap = {
            'Boston Celtics': 'Celtics',
            'Milwaukee Bucks': 'Bucks',
            'Philadelphia 76ers': '76ers',
            'Cleveland Cavaliers': 'Cavaliers',
            'New York Knicks': 'Knicks',
            'Miami Heat': 'Heat',
            'Indiana Pacers': 'Pacers',
            'Orlando Magic': 'Magic',
            'Chicago Bulls': 'Bulls',
            'Atlanta Hawks': 'Hawks',
            'Brooklyn Nets': 'Nets',
            'Toronto Raptors': 'Raptors',
            'Charlotte Hornets': 'Hornets',
            'Washington Wizards': 'Wizards',
            'Detroit Pistons': 'Pistons',
            'Oklahoma City Thunder': 'Thunder',
            'Minnesota Timberwolves': 'Timberwolves',
            'Denver Nuggets': 'Nuggets',
            'Los Angeles Clippers': 'Clippers',
            'LA Clippers': 'Clippers',
            'Phoenix Suns': 'Suns',
            'Sacramento Kings': 'Kings',
            'New Orleans Pelicans': 'Pelicans',
            'Los Angeles Lakers': 'Lakers',
            'LA Lakers': 'Lakers',
            'Golden State Warriors': 'Warriors',
            'Dallas Mavericks': 'Mavericks',
            'Houston Rockets': 'Rockets',
            'Utah Jazz': 'Jazz',
            'Memphis Grizzlies': 'Grizzlies',
            'Portland Trail Blazers': 'Trail Blazers',
            'San Antonio Spurs': 'Spurs'
        };
        
        return teamNameMap[fullName] || fullName;
    }, []);

    // Helper function to get three-letter city abbreviations for ultra-compact mobile display
    const getCityAbbreviation = useCallback((fullName) => {
        if (!fullName) return '';
        
        // Three-letter city abbreviations for NBA teams
        const cityAbbreviationMap = {
            'Boston Celtics': 'BOS',
            'Milwaukee Bucks': 'MIL',
            'Philadelphia 76ers': 'PHI',
            'Cleveland Cavaliers': 'CLE',
            'New York Knicks': 'NYK',
            'Miami Heat': 'MIA',
            'Indiana Pacers': 'IND',
            'Orlando Magic': 'ORL',
            'Chicago Bulls': 'CHI',
            'Atlanta Hawks': 'ATL',
            'Brooklyn Nets': 'BKN',
            'Toronto Raptors': 'TOR',
            'Charlotte Hornets': 'CHA',
            'Washington Wizards': 'WAS',
            'Detroit Pistons': 'DET',
            'Oklahoma City Thunder': 'OKC',
            'Minnesota Timberwolves': 'MIN',
            'Denver Nuggets': 'DEN',
            'Los Angeles Clippers': 'LAC',
            'LA Clippers': 'LAC',
            'Phoenix Suns': 'PHX',
            'Sacramento Kings': 'SAC',
            'New Orleans Pelicans': 'NOP',
            'Los Angeles Lakers': 'LAL',
            'LA Lakers': 'LAL',
            'Golden State Warriors': 'GSW',
            'Dallas Mavericks': 'DAL',
            'Houston Rockets': 'HOU',
            'Utah Jazz': 'UTA',
            'Memphis Grizzlies': 'MEM',
            'Portland Trail Blazers': 'POR',
            'San Antonio Spurs': 'SAS'
        };
        
        return cityAbbreviationMap[fullName] || fullName;
    }, []);

    // Generate dynamic Rodman query based on team performance
    const generateRodmanQuery = useCallback((team, rank) => {
        if (selectedLeague === 'NBA') {
            const playoffProb = team.playoffProb || 0;
            const eloChange = team.eloChange || 0;
            const wins = team.currentRecord?.wins || team.wins || 0;
            const losses = team.currentRecord?.losses || team.losses || 0;
            
            if (rank <= 3) {
                return `What makes ${team.teamFullName} a championship contender this season?`;
            } else if (playoffProb > 0.8) {
                return `Why is ${team.teamFullName} looking like a playoff lock?`;
            } else if (eloChange > 20) {
                return `What's driving ${team.teamFullName}'s recent surge?`;
            } else if (eloChange < -20) {
                return `What's causing ${team.teamFullName}'s recent struggles?`;
            } else if (wins > losses) {
                return `How sustainable is ${team.teamFullName}'s winning record?`;
            } else {
                return `What's the outlook for ${team.teamFullName} this season?`;
            }
        } else {
            // Premier League logic
            const position = team.position || 0;
            const top4Prob = team.top4Prob || 0;
            const points = team.points || 0;
            
            if (position <= 4) {
                return `Can ${team.teamFullName} maintain their Champions League push?`;
            } else if (top4Prob > 0.5) {
                return `What's ${team.teamFullName}'s top 4 chances?`;
            } else if (position >= 18) {
                return `How can ${team.teamFullName} avoid relegation?`;
            } else if (points > 20) {
                return `What's working for ${team.teamFullName} this season?`;
            } else {
                return `What's the season outlook for ${team.teamFullName}?`;
            }
        }
    }, [selectedLeague]);

    if (loading) {
        return (
            <div className="standings-container">
                <div className="loading-state">
                    <div className="loading-spinner"></div>
                    <h2>Loading Standings...</h2>
                </div>
            </div>
        );
    }

    if (error) {
        return (
            <div className="standings-container">
                <div className="error-state">
                    <div className="error-icon">⚡</div>
                    <h2>Unable to Load Data</h2>
                    <p>{error}</p>
                </div>
            </div>
        );
    }

    return (
        <div className="standings-container">
            {/* Header Section */}
            <header className="standings-header">
                <div className="header-content">
                    <h1 className="main-title">{selectedLeague} Power Rankings</h1>
                    <p className="subtitle">{selectedLeague === 'NBA' ? '2025-26' : '2025-26'} Season • Real-time Elo Ratings</p>
                    
                    {leagueStats && (
                        <div className="league-overview">
                            <div className="overview-stat">
                                <span className="stat-value">{leagueStats.totalTeams}</span>
                                <span className="stat-label">Teams</span>
                            </div>
                            {selectedLeague === 'NBA' ? (
                                <>
                                    <div className="overview-stat">
                                        <span className="stat-value">{leagueStats.strongContenders}</span>
                                        <span className="stat-label">Playoff Locks</span>
                                    </div>
                                    <div className="overview-stat">
                                        <span className="stat-value">{leagueStats.averageElo}</span>
                                        <span className="stat-label">Avg Elo</span>
                                    </div>
                                </>
                            ) : (
                                <>
                                    <div className="overview-stat">
                                        <span className="stat-value">{leagueStats.leader}</span>
                                        <span className="stat-label">Leader</span>
                                    </div>
                                    <div className="overview-stat">
                                        <span className="stat-value">{leagueStats.titleRace}</span>
                                        <span className="stat-label">Title Race</span>
                                    </div>
                                    <div className="overview-stat">
                                        <span className="stat-value">{leagueStats.relegationBattle}</span>
                                        <span className="stat-label">Relegation Battle</span>
                                    </div>
                                </>
                            )}
                        </div>
                    )}
                </div>
            </header>

            {/* Ask Rodman Button */}
            <div className="rodman-section">
                <Tooltip 
                    content="Get RODMAN AI's insights on current standings and surprises"
                    position="top"
                    delay={300}
                >
                    <button 
                        className="ask-rodman-btn"
                        onClick={(e) => {
                            e.preventDefault();
                            e.stopPropagation();
                            navigate('/rodman', { 
                                state: { 
                                    standingsContext: standings,
                                    league: selectedLeague,
                                    presetQuery: `Tell me about ${selectedLeague} standings - who's looking strong and what surprises do you see?`,
                                    autoStart: true
                                }
                            });
                        }}
                    >
                        ⚡ Ask RODMAN about {selectedLeague}
                    </button>
                </Tooltip>
            </div>

            {/* Controls Section */}
            <div className="controls-section">
                <div className="controls-wrapper">
                    <div className="league-selector">
                        <button
                            className={`filter-btn ${selectedLeague === 'NBA' ? 'active' : ''}`}
                            onClick={() => setSelectedLeague('NBA')}
                        >
                            NBA
                        </button>
                        <button
                            className={`filter-btn ${selectedLeague === 'Premier League' ? 'active' : ''}`}
                            onClick={() => setSelectedLeague('Premier League')}
                        >
                            Premier League
                        </button>
                    </div>

                    <div className="search-container">
                        <input
                            type="text"
                            placeholder="Search teams..."
                            value={searchQuery}
                            onChange={(e) => setSearchQuery(e.target.value)}
                            className="search-input"
                        />
                    </div>
                    
                    <div className="filter-controls">
                        {selectedLeague === 'NBA' && (
                            <div className="conference-filters">
                                {['ALL', 'East', 'West'].map(conf => (
                                    <button
                                        key={conf}
                                        className={`filter-btn ${selectedConference === conf ? 'active' : ''}`}
                                        onClick={() => setSelectedConference(conf)}
                                    >
                                        {conf}
                                    </button>
                                ))}
                            </div>
                        )}
                        
                        <select 
                            value={sortBy} 
                            onChange={(e) => setSortBy(e.target.value)}
                            className="sort-select"
                        >
                            <option value="eloRating">Power Rating</option>
                            {selectedLeague === 'NBA' ? (
                                <>
                                    <option value="playoffProb">Playoff Odds</option>
                                    <option value="wins">Win Record</option>
                                </>
                            ) : (
                                <>
                                    <option value="position">League Record</option>
                                    <option value="playoffProb">Top 4 Odds</option>
                                </>
                            )}
                            <option value="alphabetical">Alphabetical</option>
                        </select>
                    </div>
                </div>
            </div>

            {/* Results Count */}
            {/* <div className="results-info">
                <span className="results-count">
                    {processedStandings.length} team{processedStandings.length !== 1 ? 's' : ''}
                </span>
                {searchQuery && (
                    <span className="search-info">
                        for "{searchQuery}"
                    </span>
                )}
            </div> */}

            {/* Standings Grid */}
            <div className="standings-grid">
                {processedStandings.map((team, index) => {
                    const tier = getPerformanceTier(team);
                    const trend = getEloTrend(team.eloChange);
                    const isSelected = selectedTeam === team.team;
                    const rank = index + 1;

                    return (
                        <div 
                            key={team.team}
                            className={`team-row ${tier} ${trend} ${isSelected ? 'selected' : ''}`}
                            onClick={() => handleTeamClick(team.team)}
                        >
                            {/* Rank & Team */}
                            <div className="team-info">
                                <div className="rank-badge">#{rank}</div>
                                <div className="team-details">
                                    <div className="team-name-row">
                                        <h3 className="team-name">
                                            {team.visuals?.logo && (
                                                <img 
                                                    src={team.visuals.logo} 
                                                    alt=""
                                                    className="inline-logo"
                                                    onError={(e) => {
                                                        e.target.style.display = 'none';
                                                    }}
                                                />
                                            )}
                                            <span className="team-name-full">{getShortTeamName(team.teamFullName)}</span>
                                            <span className="team-name-abbrev">{getCityAbbreviation(team.teamFullName)}</span>
                                        </h3>
                                    </div>
                                    <span className="team-meta">
                                        {selectedLeague === 'NBA' ? (
                                            <>
                                                {team.conference} • {team.currentRecord?.wins || team.wins || 0}-{team.currentRecord?.losses || team.losses || 0}
                                                {team.visuals?.stadium && (
                                                    <span className="stadium-info"> • {team.visuals.stadium}</span>
                                                )}
                                            </>
                                        ) : (
                                            <>
                                                Pos: {team.position} • {team.points} pts • GD: {team.goalDifference > 0 ? '+' : ''}{team.goalDifference}
                                                {team.visuals?.stadium && (
                                                    <span className="stadium-info"> • {team.visuals.stadium}</span>
                                                )}
                                            </>
                                        )}
                                    </span>
                                </div>
                                <Tooltip 
                                    content={`Get RODMAN AI's analysis of ${team.teamFullName}'s performance and outlook`}
                                    position="left"
                                    delay={300}
                                >
                                    <button 
                                        className="ask-rodman-btn team-card"
                                        onClick={(e) => {
                                            e.stopPropagation();
                                            
                                            // Debug logging for data investigation
                                            console.group('🔍 Rodman Button Click - Team Card');
                                            console.log('Selected Team:', team.teamFullName);
                                            console.log('Team Data:', team);
                                            console.log('Rank:', rank);
                                            console.log('League:', selectedLeague);
                                            console.log('Generated Query:', generateRodmanQuery(team, rank));
                                            console.log('All Standings Data:', standings);
                                            console.log('Teams in Standings:', standings.map(t => t.teamFullName));
                                            console.groupEnd();
                                            
                                            navigate('/rodman', { 
                                                state: { 
                                                    standingsContext: standings,
                                                    league: selectedLeague,
                                                    presetQuery: generateRodmanQuery(team, rank),
                                                    focusTeam: team.teamFullName,
                                                    autoStart: true
                                                }
                                            });
                                        }}
                                    >
                                        ⚡
                                    </button>
                                </Tooltip>
                            </div>

                            {/* Elo Rating */}
                            <div className="elo-section">
                                <div className="elo-rating">{team.eloRating}</div>
                                <div className={`elo-change ${trend}`}>
                                    {team.eloChange > 0 ? '+' : ''}{team.eloChange}
                                </div>
                            </div>

                            {/* Stats */}
                            <div className="stats-section">
                                {selectedLeague === 'NBA' ? (
                                    <>
                                        <div className="stat-group">
                                            <span className="stat-label">Point Diff</span>
                                            <span className={`stat-value ${team.pointDiff >= 0 ? 'positive' : 'negative'}`}>
                                                {team.pointDiff > 0 ? '+' : ''}{team.pointDiff}
                                            </span>
                                        </div>
                                        <div className="stat-group">
                                            <span className="stat-label">Playoff Odds</span>
                                            <div className="playoff-odds-container">
                                                <span className="stat-value">{Math.round((team.playoffProb || 0) * 100)}%</span>
                                                <div className="probability-bar">
                                                    <div 
                                                        className="probability-fill"
                                                        style={{
                                                            width: `${(team.playoffProb || 0) * 100}%`,
                                                        }}
                                                    ></div>
                                                </div>
                                            </div>
                                        </div>
                                    </>
                                ) : (
                                    <>
                                        <div className="stat-group">
                                            <span className="stat-label">Recent Form</span>
                                            <div className="form-display">
                                                {(team.form && team.form !== '' ? team.form : '-----').split('').slice(-3).map((result, idx) => (
                                                    <span key={idx} className={`form-dot ${result === 'W' ? 'form-win' : result === 'D' ? 'form-draw' : result === 'L' ? 'form-loss' : 'form-none'}`}>
                                                        {result === '-' ? '·' : result}
                                                    </span>
                                                ))}
                                            </div>
                                        </div>
                                        <div className="stat-group">
                                            <span className="stat-label">Top 4 Odds</span>
                                            <div className="playoff-odds-container">
                                                <span className="stat-value">{Math.round((team.top4Prob || 0) * 100)}%</span>
                                                <div className="probability-bar">
                                                    <div 
                                                        className="probability-fill"
                                                        style={{
                                                            width: `${(team.top4Prob || 0) * 100}%`,
                                                        }}
                                                    ></div>
                                                </div>
                                            </div>
                                        </div>
                                    </>
                                )}
                            </div>

                            {/* Expanded Details */}
                            {isSelected && (
                                <div className="expanded-details">
                                    <div className="detail-grid">
                                        {selectedLeague === 'NBA' ? (
                                            <>
                                                <div className="detail-item">
                                                    <span className="detail-label">Finals Odds</span>
                                                    <span className="detail-value">{Math.round((team.finalsProb || 0) * 100)}%</span>
                                                </div>
                                                <div className="detail-item">
                                                    <span className="detail-label">Championship Odds</span>
                                                    <span className="detail-value">{Math.round((team.championshipProb || 0) * 100)}%</span>
                                                </div>
                                                <div className="detail-item">
                                                    <span className="detail-label">Projected Record</span>
                                                    <span className="detail-value">
                                                        {team.projectedRecord?.wins || 0}-{team.projectedRecord?.losses || 0}
                                                    </span>
                                                </div>
                                            </>
                                        ) : (
                                            <>
                                                <div className="detail-item">
                                                    <span className="detail-label">Title Odds</span>
                                                    <span className="detail-value">{Math.round((team.titleProb || 0) * 100)}%</span>
                                                </div>
                                                <div className="detail-item">
                                                    <span className="detail-label">Europa League</span>
                                                    <span className="detail-value">{Math.round((team.europaProb || 0) * 100)}%</span>
                                                </div>
                                                <div className="detail-item">
                                                    <span className="detail-label">Relegation Risk</span>
                                                    <span className="detail-value">{Math.round((team.relegationProb || 0) * 100)}%</span>
                                                </div>
                                                <div className="detail-item">
                                                    <span className="detail-label">Record</span>
                                                    <span className="detail-value">{team.wins}W {team.draws}D {team.losses}L</span>
                                                </div>
                                                <div className="detail-item">
                                                    <span className="detail-label">Form</span>
                                                    <span className="detail-value">{team.form || 'N/A'}</span>
                                                </div>
                                                <div className="detail-item">
                                                    <span className="detail-label">Expected Points</span>
                                                    <span className="detail-value">{team.expectedPoints || team.points}</span>
                                                </div>
                                            </>
                                        )}
                                        {team.visuals?.founded && (
                                            <div className="detail-item">
                                                <span className="detail-label">Founded</span>
                                                <span className="detail-value">{team.visuals.founded}</span>
                                            </div>
                                        )}
                                    </div>
                                </div>
                            )}
                        </div>
                    );
                })}
            </div>

            {/* No Results */}
            {processedStandings.length === 0 && (
                <div className="no-results">
                    <p>No teams found matching your criteria</p>
                    <button 
                        onClick={() => {
                            setSearchQuery('');
                            setSelectedConference('ALL');
                        }}
                        className="reset-btn"
                    >
                        Reset Filters
                    </button>
                </div>
            )}

            {/* Performance Tier Legend */}
            <div className="tier-legend-container">
                <div className="tier-legend">
                    <span className="legend-title">Performance Tiers</span>
                    <span className="legend-subtitle">
                        {selectedLeague === 'NBA' ? 'Based on Playoff Probability' : 'Based on League Position'}
                    </span>
                    <div className="legend-items">
                        {selectedLeague === 'NBA' ? (
                            <>
                                <span className="legend-item">
                                    <span className="legend-indicator elite"></span>
                                    <span className="legend-label">Elite</span>
                                    <span className="legend-range">95%+</span>
                                </span>
                                <span className="legend-item">
                                    <span className="legend-indicator strong"></span>
                                    <span className="legend-label">Strong</span>
                                    <span className="legend-range">80-94%</span>
                                </span>
                                <span className="legend-item">
                                    <span className="legend-indicator competitive"></span>
                                    <span className="legend-label">Competitive</span>
                                    <span className="legend-range">50-79%</span>
                                </span>
                                <span className="legend-item">
                                    <span className="legend-indicator developing"></span>
                                    <span className="legend-label">Developing</span>
                                    <span className="legend-range">20-49%</span>
                                </span>
                                <span className="legend-item">
                                    <span className="legend-indicator rebuilding"></span>
                                    <span className="legend-label">Rebuilding</span>
                                    <span className="legend-range">&lt;20%</span>
                                </span>
                            </>
                        ) : (
                            <>
                                <span className="legend-item">
                                    <span className="legend-indicator elite"></span>
                                    <span className="legend-label">Elite</span>
                                    <span className="legend-range">UCL (1-4)</span>
                                </span>
                                <span className="legend-item">
                                    <span className="legend-indicator strong"></span>
                                    <span className="legend-label">Strong</span>
                                    <span className="legend-range">Europa (5-7)</span>
                                </span>
                                <span className="legend-item">
                                    <span className="legend-indicator competitive"></span>
                                    <span className="legend-label">Competitive</span>
                                    <span className="legend-range">Mid-table (8-14)</span>
                                </span>
                                <span className="legend-item">
                                    <span className="legend-indicator developing"></span>
                                    <span className="legend-label">Developing</span>
                                    <span className="legend-range">Lower (15-17)</span>
                                </span>
                                <span className="legend-item">
                                    <span className="legend-indicator rebuilding"></span>
                                    <span className="legend-label">Relegation</span>
                                    <span className="legend-range">Bottom 3 (18-20)</span>
                                </span>
                            </>
                        )}
                    </div>
                    <div className="power-rating-explanation">
                        <span className="power-explanation-text">
                            Power Rating: Team strength based on performance (1500 = league average)
                        </span>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default Standings;
